import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    webrtcLogs: boolean;
  }
}

import { Tracking } from '@/shared/services/tracking';
// features imports
import useCamerasQuery from '@/features/camera/hooks/useCamerasQuery';

// websocket imports
import useAutoDirector, { AutoDirectorTuning } from './websocket/useAutoDirector';
import { useSelectCamRef } from './websocket/useSelectCam';
import { useSelectMosaicRef } from './websocket/useSelectMosaic';

import { Cameras } from '@/features/camera/types/CameraSettingsState';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';

import { AutoIsoObjectType } from '@/features/autoIso/types';
import { ServerState, ViewType } from '@/features/camera/types/VideoSource';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { GameStateManager } from '@/features/gameState/GameStateManager';
import { AutoDirector, AutoDirectorConfig } from '@/shared/types/GameStateModel';
import { GetWebsocketUrl } from '@/util';
import { usePTZStore } from '../../features/camera/store/usePTZStore';
import { TargetConstants } from '../types';
import { usePTZSocket } from './websocket/usePTZSocket';
import { useSetTrackingTargetRef } from './websocket/useTrackEntity';

export const DEFAULT_CAMERA_SETTINGS_STATE: Cameras = [];

export const MANUAL_TRACKING_OR_NO_TRACKING = {
  id: -1,
  displayName: 'Manual Tracking',
  type: 'Manual',
};
export const OBJECT_OF_PLAY = { id: 1, displayName: 'Ball/Puck', type: 'BallOrPuck' };

/**
 * Which UI surface the tracked-entity switch was made from. These are the
 * three Areas named on the analytics board; `source` is left untouched
 * because existing dashboards read it.
 */
export type TrackedEntityArea = 'Virtual Surface' | 'Players on Surface' | 'Browse All Players';

/**
 * The board's `Tracked Object` property. It names Puck and Player; this uses
 * `BallOrPuck` because the same UI serves basketball, and it matches
 * `AutoIsoObjectType`. `Official` is included because the virtual surface
 * renders officials too — counting them as Players would corrupt the very
 * split the board asked for.
 */
export type TrackedObject = 'BallOrPuck' | 'Player' | 'Official' | 'None';

/**
 * Prefer the entity's own `type`: the virtual surface passes fully-registered
 * entities whose ids come from the live tracking feed. Several call sites pass
 * a bare `{ id }`, so the id checks stay as the fallback for those.
 */
const deriveTrackedObject = (ent: any): TrackedObject => {
  switch (ent?.type) {
    case AutoIsoObjectType.BallOrPuck:
      return 'BallOrPuck';
    case AutoIsoObjectType.Player:
      return 'Player';
    case AutoIsoObjectType.Official:
      return 'Official';
    case AutoIsoObjectType.None:
    case MANUAL_TRACKING_OR_NO_TRACKING.type:
      return 'None';
    default:
      break;
  }

  if (ent?.id === MANUAL_TRACKING_OR_NO_TRACKING.id) return 'None';
  return ent?.id === OBJECT_OF_PLAY.id ? 'BallOrPuck' : 'Player';
};

export const MOSAIC_VIEW = ViewType.MOSAIC;

const DEFAULT_AUTO_DIRECTOR_CONFIG: AutoDirectorConfig = {
  d_max: 218.5,
  x_extent: 100,
  w_dist: 0.7,
  w_angle: 0.3,
  w_end: 0.5,
  hysteresis: 0.1,
  sustain: 250,
  hold: 700, // `min_hold` in the profile and the player's config file.
};

// The subset of the above that SetAutoDirectorConfig can actually carry, and
// the floor ServerStateProvider merges the server-side profile over. `d_max`
// and `x_extent` are left out because the control socket has no field for them.
export const DEFAULT_AUTO_DIRECTOR_TUNING: AutoDirectorTuning = {
  w_dist: DEFAULT_AUTO_DIRECTOR_CONFIG.w_dist,
  w_angle: DEFAULT_AUTO_DIRECTOR_CONFIG.w_angle,
  w_end: DEFAULT_AUTO_DIRECTOR_CONFIG.w_end,
  hysteresis: DEFAULT_AUTO_DIRECTOR_CONFIG.hysteresis,
  sustain: DEFAULT_AUTO_DIRECTOR_CONFIG.sustain,
  hold: DEFAULT_AUTO_DIRECTOR_CONFIG.hold,
};

export const DEFAULT_SERVER_STATE: ServerState = {
  cameras: [],
  selectedCameraId: -1,
  selectedCamera: undefined,
  viewType: ViewType.NONE,
  selectedTrackedEntity: 0,
  autoDirectorEnabled: false,
  autoDirectorAvailable: false,
  autoDirectorConfig: DEFAULT_AUTO_DIRECTOR_CONFIG,
  switch: {
    isSwitchingView: false,
    isSwitchingTrackedEntity: false,
    isSwitchingCamera: false,
    errorSwitchingCamera: false,
    errorSwitchingTrackedEntity: false,
    errorSwitchingView: false,
  },
};

// Configurable timeout settings
export interface TimeoutConfig {
  camera: number;
  view: number;
  trackedEntity: number;
}

export const DEFAULT_TIMEOUT_CONFIG: TimeoutConfig = {
  camera: 5000, // 5 seconds for camera switching
  view: 3000, // 3 seconds for view switching
  trackedEntity: 4000, // 4 seconds for tracked entity switching
};

export type ServerStateAndFunctions = {
  gameStateManager: GameStateManager | undefined;
  availableCameras: Cameras;
  trackEntity: (ent: any, receivedSource: string, area?: TrackedEntityArea) => void;
  selectCamera: (idx: number, source: string) => void;
  selectMosaic: (source: string) => void;
  singleCurrentlyTrackedObjectId: number;
  selectedCameraId: number | undefined;
  selectedCamera: Cameras[number] | undefined;
  viewType: ViewType;
  selectedObjectType?: AutoIsoObjectType;
  selectedTrackedEntity?: number;
  autoDirectorEnabled: boolean;
  autoDirectorAvailable: boolean;
  autoDirectorConfig: AutoDirectorConfig;
  /** Tuning to push to the player when a user switches auto-director on. */
  autoDirectorDefaults: AutoDirectorTuning;
  managerLoaded: boolean;
  isSwitchingCamera: boolean;
  isSwitchingView: boolean;
  isSwitchingTrackedEntity: boolean;
  hasErrorSwitchingCamera: boolean;
  hasErrorSwitchingView: boolean;
  hasErrorSwitchingTrackedEntity: boolean;
};

const ViewSourceToViewType = (source: string): ViewType => {
  switch (source) {
    case 'mosaic':
      return ViewType.MOSAIC;
    case 'single':
      return ViewType.SINGLE_CAMERA;
    default:
      return ViewType.NONE;
  }
};

/**
 * Supplies state tracking on top of the {@link useServerConnection} hook.
 * @returns functions for altering the server's state
 */
export const useServerState = (timeoutConfig: TimeoutConfig = DEFAULT_TIMEOUT_CONFIG) => {
  const websocketUrl = useRef<string | undefined>(undefined);
  const managerRef = useRef<GameStateManager | undefined>(undefined);

  // Timeout references
  const cameraTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const trackedEntityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [singleCurrentlyTrackedObjectId, setSingleCurrentlyTrackedObjectId] = useState<number>(
    MANUAL_TRACKING_OR_NO_TRACKING.id,
  );

  const { sessionState } = useAuth();
  const { surfaceWidth, surfaceHeight } = useCurrentGame();

  // Since the API currently doesn't provide a way for us to know what video source the
  // server is starting with when the app loads, we need to manage the cameras query here.
  const cameraSettingsStateQuery = useCamerasQuery();
  const [serverState, setServerState] = useState<ServerState>(DEFAULT_SERVER_STATE);

  const [viewParams, setViewParams] = useState({
    selectedCamera: -1,
    viewType: ViewType.NONE,
    selectedTrackedEntity: 0,
    cameras: DEFAULT_CAMERA_SETTINGS_STATE,
  });

  // Effect to reset camera switching state when camera selection matches
  useEffect(() => {
    if (
      serverState.selectedCameraId === viewParams.selectedCamera &&
      serverState.switch?.isSwitchingCamera
    ) {
      // Clear timeout since operation completed successfully
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current);
        cameraTimeoutRef.current = null;
      }

      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingCamera: false,
          errorSwitchingCamera: false,
        },
      }));
    }
  }, [
    serverState.selectedCamera,
    viewParams.selectedCamera,
    serverState.switch?.isSwitchingCamera,
  ]);

  // Effect to reset view switching state when view type matches
  useEffect(() => {
    if (serverState.viewType === viewParams.viewType && serverState.switch?.isSwitchingView) {
      // Clear timeout since operation completed successfully
      if (viewTimeoutRef.current) {
        clearTimeout(viewTimeoutRef.current);
        viewTimeoutRef.current = null;
      }

      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingView: false,
        },
      }));
    }
  }, [serverState.viewType, viewParams.viewType, serverState.switch?.isSwitchingView]);

  // Effect to reset tracked entity switching state when entity matches
  useEffect(() => {
    if (
      serverState.selectedTrackedEntity === viewParams.selectedTrackedEntity &&
      serverState.switch?.isSwitchingTrackedEntity
    ) {
      // Clear timeout since operation completed successfully
      if (trackedEntityTimeoutRef.current) {
        clearTimeout(trackedEntityTimeoutRef.current);
        trackedEntityTimeoutRef.current = null;
      }

      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingTrackedEntity: false,
        },
      }));
    }
  }, [
    serverState.selectedTrackedEntity,
    viewParams.selectedTrackedEntity,
    serverState.switch?.isSwitchingTrackedEntity,
  ]);

  const [managerLoaded, setManagerLoaded] = useState(false);

  // Mirrors serverState.autoDirectorEnabled for the stable selectCamera callback.
  const autoDirectorEnabledRef = useRef(false);
  useEffect(() => {
    autoDirectorEnabledRef.current = serverState.autoDirectorEnabled;
  }, [serverState.autoDirectorEnabled]);

  //websocket operations
  const [, setSelectCam] = useSelectCamRef();
  const [, setSelectMosaic] = useSelectMosaicRef();
  const [, setTrackingTarget] = useSetTrackingTargetRef();

  const { init: initPTZStore } = usePTZStore();
  const { setValue: enterManualMode } = usePTZSocket();
  const { disableAutoDirector } = useAutoDirector();

  // handleChange on the State
  const { handleChange: updatePTZstore } = usePTZStore();

  const selectCamera = useCallback(async (idx: number, source: string) => {
    // Clear any existing timeout
    if (cameraTimeoutRef.current) {
      clearTimeout(cameraTimeoutRef.current);
    }

    // A camera pick supersedes auto-director. The player only restarts its shot
    // clock on SelectCam and would cut away again, so switch it off here for
    // every caller (toolbar, keyboard shortcut, surface click, fallback).
    if (autoDirectorEnabledRef.current) {
      disableAutoDirector();
    }

    // Set intermediate switching state
    setServerState((prevState) => ({
      ...prevState,
      switch: {
        ...prevState.switch,
        isSwitchingCamera: true,
        errorSwitchingCamera: false,
      },
    }));

    // Set timeout for tracked entity switching
    cameraTimeoutRef.current = setTimeout(() => {
      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingCamera: false,
          errorSwitchingCamera: true,
        },
      }));
      console.error(`Tracked entity switching timed out after ${timeoutConfig.trackedEntity}ms`);
    }, timeoutConfig.camera);

    try {
      if (setSelectCam) {
        setSelectCam(idx);
        setViewParams((prev) => ({
          ...prev,
          selectedCamera: idx,
          viewType: ViewSourceToViewType('single'),
        }));
      }

      Tracking.getInstance().track('Switch View', {
        source,
        category: 'Controls',
        type: 'single',
      });
    } catch (error) {
      // Set error state if camera selection fails
      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingCamera: false,
          errorSwitchingCamera: true,
        },
      }));
      throw error;
    }
  }, []);

  const trackEntity = useCallback(
    (ent: any, receivedSource: string, area?: TrackedEntityArea) => {
      let source: string = receivedSource;

      if (!ent) throw new Error('Entity must be defined');
      if (!ent.id) throw new Error('Entity must have an id');

      const { id } = ent;

      // Clear any existing timeout
      if (trackedEntityTimeoutRef.current) {
        clearTimeout(trackedEntityTimeoutRef.current);
      }

      // Set intermediate switching state
      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingTrackedEntity: true,
          errorSwitchingTrackedEntity: false,
        },
      }));

      // Set timeout for tracked entity switching
      trackedEntityTimeoutRef.current = setTimeout(() => {
        setServerState((prevState) => ({
          ...prevState,
          switch: {
            ...prevState.switch,
            isSwitchingTrackedEntity: false,
            errorSwitchingTrackedEntity: true,
          },
        }));
        console.error(`Tracked entity switching timed out after ${timeoutConfig.trackedEntity}ms`);
      }, timeoutConfig.trackedEntity);

      try {
        if (ent.id === MANUAL_TRACKING_OR_NO_TRACKING.id) {
          source = 'Manual Tracking';
          enterManualMode({ pan: 0, tilt: 0, zoom: 0 });
          setViewParams((prev) => ({
            ...prev,
            viewType: ViewType.NONE,
            selectedTrackedEntity: id,
          }));
        } else {
          if (setTrackingTarget) {
            setTrackingTarget(id);
            setViewParams((prev) => ({
              ...prev,
              selectedTrackedEntity: id,
            }));
          }
        }

        if (source) {
          Tracking.getInstance().track('Switch Tracked Entity', {
            category: 'Iso',
            source,
            area,
            trackedObject: deriveTrackedObject(ent),
            ent,
          });
        }
      } catch (error) {
        // Clear timeout and set error state if tracking fails immediately
        if (trackedEntityTimeoutRef.current) {
          clearTimeout(trackedEntityTimeoutRef.current);
          trackedEntityTimeoutRef.current = null;
        }
        setServerState((prevState) => ({
          ...prevState,
          switch: {
            ...prevState.switch,
            isSwitchingTrackedEntity: false,
            errorSwitchingTrackedEntity: true,
          },
        }));
        throw error;
      }
    },
    [timeoutConfig.trackedEntity],
  );

  const selectMosaic = useCallback(
    (source: string) => {
      // Clear any existing timeout
      if (viewTimeoutRef.current) {
        clearTimeout(viewTimeoutRef.current);
      }

      // Set intermediate switching state
      setServerState((prevState) => ({
        ...prevState,
        switch: {
          ...prevState.switch,
          isSwitchingView: true,
          errorSwitchingView: false,
        },
      }));

      // Set timeout for view switching
      viewTimeoutRef.current = setTimeout(() => {
        setServerState((prevState) => ({
          ...prevState,
          switch: {
            ...prevState.switch,
            isSwitchingView: false,
            errorSwitchingView: true,
          },
        }));
        console.error(`View switching timed out after ${timeoutConfig.view}ms`);
      }, timeoutConfig.view);

      try {
        if (setSelectMosaic) {
          setSelectMosaic({ dims: { width: 0, height: 0 }, idxs: [] });
          setViewParams((prev) => ({
            ...prev,
            viewType: ViewSourceToViewType('mosaic'),
            selectedCamera: -1,
          }));
        }

        Tracking.getInstance().track('Switch View', {
          source,
          category: 'Controls',
          type: 'mosaic',
        });
      } catch (error) {
        // Clear timeout and set error state if mosaic selection fails immediately
        if (viewTimeoutRef.current) {
          clearTimeout(viewTimeoutRef.current);
          viewTimeoutRef.current = null;
        }
        setServerState((prevState) => ({
          ...prevState,
          switch: {
            ...prevState.switch,
            isSwitchingView: false,
            errorSwitchingView: true,
          },
        }));
        throw error;
      }
    },
    [timeoutConfig.view],
  );

  useEffect(() => {
    // Only proceed if session is ready AND cameras have been successfully fetched
    if (sessionState !== LocalSessionState.READY) return;
    if (cameraSettingsStateQuery.status !== 'success') return;
    if (!cameraSettingsStateQuery.data || cameraSettingsStateQuery.data.length === 0) return;

    // Cameras are loaded, now create and connect the manager
    websocketUrl.current = GetWebsocketUrl('/state/0.1.0');
    const manager = new GameStateManager(websocketUrl.current);
    manager.setSurfaceDimensions(surfaceWidth, surfaceHeight);
    managerRef.current = manager;

    // Define event handlers (using functional setState to avoid stale closures)
    const handleVcamChange = (vcam: any) => {
      setServerState((currentState) => {
        const { selectedCameraId } = currentState;
        if (!updatePTZstore || typeof selectedCameraId === 'undefined') return currentState;
        updatePTZstore(vcam, selectedCameraId);
        return currentState;
      });
    };

    const handleViewChange = (view: any) => {
      const { type, idx: cameraIndex } = view;
      const selectedIdx = typeof cameraIndex === 'number' ? cameraIndex : parseInt(cameraIndex, 10);

      setServerState((currentState) => {
        const newState = {
          ...currentState,
          selectedCameraId: selectedIdx,
          viewType: ViewSourceToViewType(type),
        };
        // Prevent unnecessary state updates
        return JSON.stringify(currentState) !== JSON.stringify(newState) ? newState : currentState;
      });
    };

    const handleTrackingEntityUpdated = (target: any) => {
      switch (target.type) {
        case TargetConstants.AUTOMATIC:
          if (!target.id) throw new Error('Automatic tracking target must have an id');

          setSingleCurrentlyTrackedObjectId((currentId) =>
            currentId !== target.id ? target.id : currentId,
          );
          setServerState((currentState) => {
            const newSelectedObjectType =
              target.id === 1 ? AutoIsoObjectType.BallOrPuck : AutoIsoObjectType.Player;
            const updatedCameras =
              currentState.cameras?.map((camera) => ({
                ...camera,
                trackingId: target.id,
              })) || currentState.cameras;

            const camerasChanged =
              JSON.stringify(currentState.cameras) !== JSON.stringify(updatedCameras);
            const objectTypeChanged = currentState.selectedObjectType !== newSelectedObjectType;
            const trackedEntityChanged = currentState.selectedTrackedEntity !== target.id;

            // Update state if any of the tracked values have changed
            return camerasChanged || objectTypeChanged || trackedEntityChanged
              ? {
                  ...currentState,
                  cameras: updatedCameras,
                  selectedObjectType: newSelectedObjectType,
                  selectedTrackedEntity: target.id,
                }
              : currentState;
          });
          break;
        case TargetConstants.MANUAL:
          setSingleCurrentlyTrackedObjectId(MANUAL_TRACKING_OR_NO_TRACKING.id);
          setServerState((currentState) => ({
            ...currentState,
            selectedTrackedEntity: MANUAL_TRACKING_OR_NO_TRACKING.id,
            selectedObjectType: AutoIsoObjectType.None,
          }));
          break;
        default:
          break;
      }
    };

    const handleAutoDirectorChange = (autoDirector?: AutoDirector) => {
      const available = autoDirector != null;
      const enabled = autoDirector?.enabled ?? false;
      const config = autoDirector?.config ?? DEFAULT_AUTO_DIRECTOR_CONFIG;

      setServerState((currentState) =>
        currentState.autoDirectorAvailable === available &&
        currentState.autoDirectorEnabled === enabled &&
        currentState.autoDirectorConfig.d_max === config.d_max &&
        currentState.autoDirectorConfig.x_extent === config.x_extent &&
        currentState.autoDirectorConfig.w_dist === config.w_dist &&
        currentState.autoDirectorConfig.w_angle === config.w_angle &&
        currentState.autoDirectorConfig.w_end === config.w_end &&
        currentState.autoDirectorConfig.hysteresis === config.hysteresis &&
        currentState.autoDirectorConfig.sustain === config.sustain &&
        currentState.autoDirectorConfig.hold === config.hold
          ? currentState
          : {
              ...currentState,
              autoDirectorAvailable: available,
              autoDirectorEnabled: enabled,
              autoDirectorConfig: config,
            },
      );
    };

    // Attach event listeners BEFORE connecting
    managerRef.current.on('vcamChange', handleVcamChange);
    managerRef.current.on('viewChange', handleViewChange);
    managerRef.current.on('trackingTargetChange', handleTrackingEntityUpdated);
    managerRef.current.on('autoDirectorChange', handleAutoDirectorChange);

    managerRef.current.on('connected', () => {
      setManagerLoaded(true);
    });

    managerRef.current.on('disconnected', () => {
      setManagerLoaded(false);
    });

    // Now connect (listeners are already attached)
    managerRef.current.connect();

    return () => {
      if (managerRef.current) {
        managerRef.current.off('vcamChange', handleVcamChange);
        managerRef.current.off('viewChange', handleViewChange);
        managerRef.current.off('trackingTargetChange', handleTrackingEntityUpdated);
        managerRef.current.off('autoDirectorChange', handleAutoDirectorChange);
        managerRef.current.disconnect();
        managerRef.current = undefined;
      }
    };
  }, [sessionState, cameraSettingsStateQuery.status, cameraSettingsStateQuery.data]);

  // Push surface dimensions to the manager when league metadata arrives after construction.
  useEffect(() => {
    managerRef.current?.setSurfaceDimensions(surfaceWidth, surfaceHeight);
  }, [surfaceWidth, surfaceHeight]);

  useEffect(() => {
    if (cameraSettingsStateQuery.status === 'success' && cameraSettingsStateQuery.data) {
      initPTZStore(cameraSettingsStateQuery.data);

      setServerState((currentState) => ({
        ...currentState,
        cameras: cameraSettingsStateQuery.data || DEFAULT_CAMERA_SETTINGS_STATE,
      }));
    }
  }, [cameraSettingsStateQuery.status, cameraSettingsStateQuery.data]);

  // Update selectedCamera when cameras or selectedCameraId changes
  useEffect(() => {
    if (Array.isArray(serverState.cameras) && serverState.cameras.length > 0) {
      const camera = serverState.cameras.find(
        (cam) => cam.camIndex === serverState.selectedCameraId,
      );
      if (camera !== serverState.selectedCamera) {
        setServerState((currentState) => ({
          ...currentState,
          selectedCamera: camera,
        }));
      }
    }
  }, [serverState.cameras, serverState.selectedCameraId]);

  // Auto-fallback: when the currently selected camera goes offline (e.g. via an
  // SSE camera-status-update that flips isLive to false), pick the next live
  // camera in list order, wrapping around to the start of the list. If no live
  // cameras remain, the selection is left untouched so the UI keeps showing the
  // previously-selected (now-disabled) camera; a later SSE update bringing a
  // camera back live will re-trigger this effect.
  useEffect(() => {
    const cameras = serverState.cameras;
    const currentId = serverState.selectedCameraId;
    if (!Array.isArray(cameras) || cameras.length === 0) return;
    if (typeof currentId !== 'number' || currentId < 0) return;

    const currentIdx = cameras.findIndex((c) => c.camIndex === currentId);
    if (currentIdx === -1) return;
    if (cameras[currentIdx].isLive) return;

    for (let i = 1; i <= cameras.length; i++) {
      const candidate = cameras[(currentIdx + i) % cameras.length];
      if (candidate.isLive) {
        Tracking.getInstance().track('Auto-Switch Camera', {
          camera: candidate.camIndex,
          reason: 'previous-camera-disabled',
          category: 'Playback',
        });
        selectCamera(candidate.camIndex, 'auto-fallback: previous camera disabled');
        return;
      }
    }
  }, [serverState.cameras, serverState.selectedCameraId, selectCamera]);

  const serverStateAndFunctions: ServerStateAndFunctions = {
    gameStateManager: managerRef.current,
    // Ensure availableCameras is always an array
    availableCameras: Array.isArray(serverState.cameras) ? serverState.cameras : [],
    trackEntity,
    selectCamera,
    selectMosaic,
    singleCurrentlyTrackedObjectId, // DEPRECATED: use `selectedTrackedEntity` instead
    selectedCameraId: serverState.selectedCameraId,
    selectedCamera: serverState.selectedCamera,
    viewType: serverState.viewType,
    selectedObjectType: serverState.selectedObjectType,
    selectedTrackedEntity: serverState.selectedTrackedEntity,
    autoDirectorEnabled: serverState.autoDirectorEnabled,
    autoDirectorAvailable: serverState.autoDirectorAvailable,
    autoDirectorConfig: serverState.autoDirectorConfig,
    // Overridden by ServerStateProvider with the ops-owned profile; a direct
    // useServerState() caller keeps the built-in tuning.
    autoDirectorDefaults: DEFAULT_AUTO_DIRECTOR_TUNING,
    managerLoaded,
    isSwitchingCamera: serverState.switch.isSwitchingCamera,
    isSwitchingView: serverState.switch.isSwitchingView,
    isSwitchingTrackedEntity: serverState.switch.isSwitchingTrackedEntity,
    hasErrorSwitchingCamera: serverState.switch.errorSwitchingCamera,
    hasErrorSwitchingView: serverState.switch.errorSwitchingView,
    hasErrorSwitchingTrackedEntity: serverState.switch.errorSwitchingTrackedEntity,
  };

  const serverStateAndFunctionsCopyRef = useRef(serverStateAndFunctions);

  useEffect(() => {
    serverStateAndFunctionsCopyRef.current = serverStateAndFunctions;
  }, [serverStateAndFunctions]);

  const getServerStateAndFunctions = useCallback(() => serverStateAndFunctionsCopyRef.current, []);

  return {
    ...serverStateAndFunctions,
    getServerStateAndFunctions,
  };
};

export default useServerState;
